import type { Message, ReasoningTraceEntry, ToolInvocation } from '@/components/chat/chat-types';
import { toArray } from '@/lib/utils/to-array';
import { requestWindowAnimationFrame } from '@/lib/utils/window-timers';
import type { ScrollToBottomFn as ScrollToBottomFunction } from './types';

type ToolCallUpdate = {
  toolCallId: string;
  toolName: string;
  args?: Record<string, unknown> | undefined;
  state?: ToolInvocation['state'];
};

type ToolResultUpdate = {
  toolCallId: string;
  result?: Record<string, unknown> | string | number | null | unknown[] | undefined;
  state?: ToolInvocation['state'];
};

type CompleteOptions = {
  fallbackContent?: string;
  clearToolInvocations?: boolean;
};

export const appendAssistantDelta = (messages: Message[], assistantId: string, delta: string): Message[] =>
  messages.map((message) =>
    message.id === assistantId ? { ...message, content: `${message.content}${delta}` } : message
  );

export const upsertToolInvocation = (messages: Message[], assistantId: string, update: ToolCallUpdate): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    const existing = toArray(message.toolInvocations);
    const arguments_ = update.args ?? {};
    const existingInvocation = existing.find((invocation) => invocation.toolCallId === update.toolCallId);

    if (!existingInvocation) {
      return {
        ...message,
        toolInvocations: [
          ...existing,
          {
            toolCallId: update.toolCallId,
            toolName: update.toolName,
            args: arguments_,
            state: update.state ?? 'call',
          },
        ],
      };
    }

    const previousArguments = existingInvocation.args ?? {};
    const mergedArguments = { ...previousArguments, ...arguments_ };
    const nextInvocations = existing.map((invocation) => {
      if (invocation.toolCallId !== update.toolCallId) {
        return invocation;
      }
      const updated: ToolInvocation = {
        ...invocation,
        toolName: update.toolName ?? invocation.toolName,
        args: mergedArguments,
        state: update.state ?? invocation.state,
      };
      return updated;
    });
    return { ...message, toolInvocations: nextInvocations };
  });

export const applyToolResult = (messages: Message[], assistantId: string, result: ToolResultUpdate): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    const invocations = message.toolInvocations?.map((invocation) => {
      if (invocation.toolCallId !== result.toolCallId) {
        return invocation;
      }
      const nextState = result.state ?? (result.result === undefined ? invocation.state : 'result');
      const nextResult = result.result === undefined ? invocation.result : result.result;
      return {
        ...invocation,
        result: nextResult,
        state: nextState,
      };
    });
    return {
      ...message,
      toolInvocations: invocations,
    };
  });

export const appendReasoningTraceEntry = (
  messages: Message[],
  assistantId: string,
  entry: ReasoningTraceEntry
): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    const existing = toArray(message.reasoningTrace);
    return {
      ...message,
      reasoningTrace: [...existing, entry],
    };
  });

export const updateReasoningTraceEntry = (
  messages: Message[],
  assistantId: string,
  entryId: string,
  updater: (entry: ReasoningTraceEntry) => ReasoningTraceEntry
): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    const trace = message.reasoningTrace;
    if (!trace || trace.length === 0) {
      return message;
    }
    if (!trace.some((entry) => entry.id === entryId)) {
      return message;
    }
    const nextTrace = trace.map((existingEntry) =>
      existingEntry.id === entryId ? updater(existingEntry) : existingEntry
    );
    return {
      ...message,
      reasoningTrace: nextTrace,
    };
  });

export const completeAssistantMessage = (
  messages: Message[],
  assistantId: string,
  options: CompleteOptions = {}
): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    if (message.status && message.status !== 'streaming') {
      return message;
    }
    const nextContent = message.content.length > 0 ? message.content : (options.fallbackContent ?? message.content);
    const nextToolInvocations = options.clearToolInvocations ? undefined : message.toolInvocations;
    return {
      ...message,
      content: nextContent,
      status: 'complete',
      toolInvocations: nextToolInvocations,
    };
  });

export const setAssistantErrorMessage = (
  messages: Message[],
  assistantId: string,
  friendlyMessage: string
): Message[] =>
  messages.map((message) => {
    if (message.id !== assistantId) {
      return message;
    }
    if (message.status && message.status !== 'streaming') {
      return message;
    }
    return {
      ...message,
      content: friendlyMessage,
      status: 'complete',
      toolInvocations: undefined,
    };
  });

export const scheduleInitialScroll = (scrollToBottom: ScrollToBottomFunction, behavior: ScrollBehavior) => {
  const firstHandle = requestWindowAnimationFrame(() => {
    const secondHandle = requestWindowAnimationFrame(() => {
      scrollToBottom(behavior);
    });
    if (secondHandle === null) {
      scrollToBottom(behavior);
    }
  });
  if (firstHandle === null) {
    scrollToBottom('auto');
  }
};
